import { z } from 'zod'

/**
 * Datenmodell der Taschenkarten.
 *
 * `CardMetaSchema` beschreibt, was die Redaktion in `content/cards/<slug>/card.yaml`
 * pflegt. `CatalogCardSchema` ergänzt die Felder, die die Content-Pipeline beim Build
 * errechnet (Hash, Seitenzahl, Thumbnail, …). Beide werden von Build-Skript und App
 * gemeinsam genutzt, damit Erzeuger und Verbraucher nicht auseinanderlaufen können.
 */

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export const ORGANIZATIONS = [
  'feuerwehr',
  'rettungsdienst',
  'bereitschaften',
  'thw',
  'polizei',
  'bos-uebergreifend',
] as const

export const ORGANIZATION_LABELS: Record<Organization, string> = {
  feuerwehr: 'Feuerwehr',
  rettungsdienst: 'Rettungsdienst',
  bereitschaften: 'Bereitschaften',
  thw: 'THW',
  polizei: 'Polizei',
  'bos-uebergreifend': 'BOS-übergreifend',
}

export type Organization = (typeof ORGANIZATIONS)[number]

export const CardMetaSchema = z
  .object({
    /** Stabile Kennung — steuert Favoriten und Deeplinks und darf sich nie ändern. */
    id: z.string().regex(SLUG, 'muss ein Kleinbuchstaben-Slug sein, z. B. "atemschutz-ueberwachung"'),
    title: z.string().min(3).max(80),
    subtitle: z.string().max(160).optional(),
    organization: z.enum(ORGANIZATIONS),
    /** Muss in content/taxonomy.yaml definiert sein. */
    category: z.string().regex(SLUG),
    tags: z.array(z.string().regex(SLUG)).default([]),
    /** Synonyme und Abkürzungen, die im PDF-Text nicht vorkommen ("PA", "MANV"). */
    keywords: z.array(z.string().min(1)).default([]),
    version: z.string().min(1),
    updatedAt: z.iso.date('erwartet JJJJ-MM-TT'),
    source: z.string().min(2),
    license: z.string().min(2),
    /** IDs verwandter Karten — werden beim Build gegen den Katalog geprüft. */
    related: z.array(z.string().regex(SLUG)).default([]),
  })
  .strict()

export type CardMeta = z.infer<typeof CardMetaSchema>

export const CatalogCardSchema = CardMetaSchema.extend({
  /** Pfad der PDF relativ zur App-Basis. */
  pdfPath: z.string(),
  /** SHA-256 der PDF — Grundlage der Update-Erkennung im Offline-Sync. */
  pdfHash: z.string().length(64),
  pdfBytes: z.number().int().positive(),
  pageCount: z.number().int().positive(),
  /** Vorschaubild der ersten Seite, relativ zur App-Basis. */
  thumb: z.string(),
  orientation: z.enum(['portrait', 'landscape']),
})

export type CatalogCard = z.infer<typeof CatalogCardSchema>

export const CategorySchema = z
  .object({
    id: z.string().regex(SLUG),
    label: z.string().min(2),
    description: z.string().optional(),
    /** Sortierreihenfolge im Katalog; kleinere Werte zuerst. */
    order: z.number().int().default(100),
  })
  .strict()

export type Category = z.infer<typeof CategorySchema>

/**
 * Klartext zu einem Schlagwort.
 *
 * Die Karten führen Schlagwörter als Slug ("fwdv-7", "th-vu"). In der Suche stehen sie
 * als Vorschlag vor dem Nutzer, und dort taugt "Fwdv 7" nicht. Fehlt ein Eintrag, bildet
 * die App den Slug notdürftig ab und der Build weist darauf hin — anders als bei
 * Kategorien bricht er nicht ab: Ein neu vergebenes Schlagwort soll keine Auslieferung
 * aufhalten.
 */
export const TagSchema = z
  .object({
    id: z.string().regex(SLUG),
    label: z.string().min(2),
  })
  .strict()

export type Tag = z.infer<typeof TagSchema>

export const TaxonomySchema = z
  .object({
    categories: z.array(CategorySchema).min(1),
    tags: z.array(TagSchema).default([]),
  })
  .strict()

export const CatalogSchema = z
  .object({
    /** Schema-Version des Katalogs; erlaubt der App, alte Caches zu verwerfen. */
    schemaVersion: z.literal(1),
    /** Zeitpunkt des Builds (ISO), nur zur Anzeige. */
    generatedAt: z.string(),
    /** Hash über alle Karten-Hashes — erkennt Katalogänderungen mit einem Vergleich. */
    catalogHash: z.string().length(64),
    categories: z.array(CategorySchema),
    /** Klartext der tatsächlich vergebenen Schlagwörter. `default` hält ältere
     *  Katalogfassungen aus dem Offline-Cache lesbar, die das Feld noch nicht führen. */
    tags: z.array(TagSchema).default([]),
    cards: z.array(CatalogCardSchema),
  })
  .strict()

export type Catalog = z.infer<typeof CatalogSchema>
